import Api from '@teko/api';
import { accessTokenInterceptor } from '@teko/auth';
import { SORT_ORDER, SORT_TYPE } from './constants.es.js';
import { normalizePageBuilderToDiscoveryParams } from './helpers.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
class DiscoveryService {
  constructor(discoveryApiUrl, terminalId, terminalCode, language, getCustomAccessToken) {
    this._isArrayValue = [
      "sellerCodes",
      "brands",
      "categories",
      "categoryIds",
      "tags",
      "providerIds",
      "sellerIds",
      "skus",
      "masterCategories",
      "masterCategoryIds",
      "excludedSkus",
      "returnFilterable",
      "excludedSkus",
      "clearanceTypeIds"
    ];
    this._discoveryPayloadKeys = [
      "terminalCode",
      "terminalId",
      "query",
      "filter",
      "pagination",
      "sorting",
      "userId",
      "phoneNumber",
      "location",
      "returnFilterable",
      "block",
      "pos365StoreCode",
      "slug",
      "terminalId"
    ];
    this._discoveryBlockKeys = ["blockId", "itemId"];
    this._discoveryFilterKeys = [
      "sellingCodes",
      "priceLte",
      "priceGte",
      "brands",
      "categories",
      "categoryIds",
      "tags",
      "hasPromotions",
      "attributes",
      "providerIds",
      "sellerIds",
      "skus",
      "flashSaleType",
      "masterCategories",
      "masterCategoryIds",
      "excludedSkus",
      "clearanceTypeIds",
      "returnAllVariants"
    ];
    this._isBooleanValue = ["hasPromotions", "returnAllVariants"];
    this._isNumberValue = ["clearanceTypeIds", "categoryIds"];
    this._discoverySortingKeys = ["sort", "order"];
    this._discoveryPaginationKeys = ["pageNumber", "itemsPerPage"];
    this._orderValuesMap = {
      desc: SORT_ORDER.ORDER_BY_DESCENDING,
      asc: SORT_ORDER.ORDER_BY_ASCENDING,
      DESC: SORT_ORDER.ORDER_BY_DESCENDING,
      ASC: SORT_ORDER.ORDER_BY_ASCENDING
    };
    this._sortValuesMap = {
      new: "SORT_BY_PUBLISH_AT",
      bestPrice: "SORT_BY_PRICE",
      "view.last_3_day": SORT_TYPE.SORT_BY_MOST_VIEW,
      "view.last_7_day": SORT_TYPE.SORT_BY_MOST_VIEW_7_DAYS,
      "view.last_30_day": SORT_TYPE.SORT_BY_MOST_VIEW_30_DAYS,
      "view.last_90_day": SORT_TYPE.SORT_BY_MOST_VIEW_90_DAYS,
      "quantity.last_3_day": SORT_TYPE.SORT_BY_TOP_SALE_QUANTITY,
      "quantity.last_1_week": SORT_TYPE.SORT_BY_TOP_SALE_QUANTITY_7_DAYS,
      "quantity.last_1_month": SORT_TYPE.SORT_BY_TOP_SALE_QUANTITY_30_DAYS,
      "quantity.last_3_month": SORT_TYPE.SORT_BY_TOP_SALE_QUANTITY_90_DAYS,
      discountPercent: SORT_TYPE.SORT_BY_DISCOUNT_PERCENT,
      discount: SORT_TYPE.SORT_BY_DISCOUNT_PERCENT
    };
    this.discoveryApi = new Api(discoveryApiUrl);
    if (typeof getCustomAccessToken === "function") {
      this.discoveryApi.addInterceptor((request) => {
        request.headers.set("Authorization", `Bearer ${getCustomAccessToken()}`);
      });
    } else {
      this.discoveryApi.addInterceptor(accessTokenInterceptor);
    }
    this.discoveryApi.addInterceptor((request) => {
      request.headers.set("Accept-Language", language || "*");
    });
    this.terminalId = terminalId;
    this.terminalCode = terminalCode;
  }
  _cookPayload(params) {
    const payload = __spreadProps(__spreadValues(__spreadValues({
      filter: {},
      pagination: {},
      query: "",
      sorting: {},
      returnFilterable: [],
      block: {},
      slug: ""
    }, this.terminalCode ? { terminalCode: this.terminalCode } : {}), this.terminalId ? { terminalId: this.terminalId } : {}), {
      fieldMask: []
    });
    const normalizedParams = normalizeDiscoveryApiParams(normalizePageBuilderToDiscoveryParams(params));
    const attributes = [];
    Object.keys(normalizedParams).forEach((key) => {
      let data = this._isArrayValue.includes(key) ? convertArrayValue(normalizedParams[key], this._isNumberValue.includes(key) ? "number" : "string") : normalizedParams[key];
      if (key.startsWith("attributes.")) {
        attributes.push({
          code: key.replace("attributes.", ""),
          optionIds: data.split(",")
        });
      }
      if (this._isBooleanValue.includes(key)) {
        data = data === "true" || data === true;
      }
      if (this._discoveryPayloadKeys.includes(key)) {
        payload[key] = data;
      }
      if (this._discoveryBlockKeys.includes(key)) {
        payload.block[key] = data;
      }
      if (this._discoveryFilterKeys.includes(key)) {
        payload.filter = __spreadProps(__spreadValues({}, payload.filter), {
          [key]: data
        });
      }
      if (this._discoverySortingKeys.includes(key)) {
        payload.sorting = __spreadValues(__spreadValues({}, payload.sorting), {
          [key]: key === "order" ? this._orderValuesMap[data] || data || "ORDER_BY_UNSPECIFIED" : this._sortValuesMap[data] || data || "SORT_BY_UNSPECIFIED"
        });
      }
      if (this._discoveryPaginationKeys.includes(key)) {
        payload.pagination = __spreadValues(__spreadValues({}, payload.pagination), {
          [key]: data
        });
      }
    });
    payload["filter"]["attributes"] = [
      ...payload["filter"]["attributes"] || [],
      ...attributes
    ];
    if (!!params.fieldMask) {
      payload.fieldMask = params.fieldMask;
    }
    return payload;
  }
  setTerminal({
    terminalCode,
    terminalId
  }) {
    this.terminalCode = terminalCode;
    this.terminalId = terminalId;
  }
  getBaseUrl() {
    return this.discoveryApi.baseURL;
  }
  async search(params) {
    const searchParams = this._cookPayload(params);
    const { data } = await this.discoveryApi.post("/search", searchParams);
    return data;
  }
  async internalSearch(terminalCode, siteId, query, limit, sorting) {
    var _a;
    const { data } = await this.discoveryApi.post("/internal/search", {
      terminalCode,
      siteId,
      query,
      sorting,
      limit
    });
    if ((data == null ? void 0 : data.code) !== "0" || !((_a = data == null ? void 0 : data.data) == null ? void 0 : _a.products)) {
      return [];
    }
    return data.data.products;
  }
  async getProductDetailBySkus(params) {
    const LIMIT_SKUS = 50;
    const promises = [];
    const _a = params, { skus } = _a, rest = __objRest(_a, ["skus"]);
    const skuArray = typeof skus === "string" ? skus.split(",") : skus;
    for (let index = 0; index < skuArray.length; index += LIMIT_SKUS) {
      const subSkus = skuArray.slice(index, index + LIMIT_SKUS);
      promises.push(this.discoveryApi.get("/products", {
        params: __spreadProps(__spreadValues({
          skus: subSkus
        }, rest), {
          terminalCode: this.terminalCode || params.terminalCode,
          terminalId: this.terminalId || params.terminalId
        })
      }).then((response) => {
        var _a2, _b;
        return ((_b = (_a2 = response.data) == null ? void 0 : _a2.result) == null ? void 0 : _b.products) || [];
      }));
    }
    const products = await Promise.all(promises).then((values) => values.reduce((acc, curr) => [...acc, ...curr], []));
    return products;
  }
  async getProduct(params) {
    var _a;
    const { data } = await this.discoveryApi.get(`/product`, {
      params: __spreadProps(__spreadValues({}, params), {
        terminalCode: this.terminalCode,
        terminalId: this.terminalId
      })
    });
    return (_a = data == null ? void 0 : data.result) == null ? void 0 : _a.product;
  }
  async getKeyword(params) {
    const { data } = await this.discoveryApi.get("/keywords", {
      params: __spreadProps(__spreadValues({}, params), {
        terminalCode: this.terminalCode,
        terminalId: this.terminalId
      })
    });
    return data;
  }
  async getBlock(params, timeout) {
    const { data } = await this.discoveryApi.get(`/blocks`, {
      params: __spreadProps(__spreadValues({}, params), {
        terminalCode: this.terminalCode,
        terminalId: this.terminalId
      }),
      timeout
    });
    return data;
  }
  async getPopularKeyword(params) {
    const { data } = await this.discoveryApi.get(`/recommended-search-terms`, {
      params: __spreadProps(__spreadValues({}, params), {
        terminalCode: this.terminalCode,
        terminalId: this.terminalId
      })
    });
    return data;
  }
  async getRecommendationProducts(sku) {
    const { data } = await this.discoveryApi.get(`/products/${sku}/recommendations`, {
      params: {
        terminalId: this.terminalId,
        terminalCode: this.terminalCode
      }
    });
    return data;
  }
  async getRecommendationCombos(sku, params) {
    const { data } = await this.discoveryApi.get(`/products/${sku}/combos`, {
      params: __spreadProps(__spreadValues({}, params), {
        terminalCode: this.terminalCode,
        terminalId: this.terminalId
      })
    });
    return data;
  }
  async getDetailComboRecommendation(params) {
    const { data } = await this.discoveryApi.post(`/combos`, __spreadProps(__spreadValues({}, params), {
      terminalId: this.terminalId,
      terminalCode: this.terminalCode
    }));
    return data;
  }
  async getMenuItems(zone) {
    const result = await this.discoveryApi.get(`/menu-items`, {
      params: {
        terminalId: this.terminalId,
        terminal: this.terminalCode,
        zone,
        _limit: 1e3,
        _sort: "priority",
        _order: "asc",
        fieldMask: [
          "id",
          "name",
          "parentId",
          "icon",
          "isTargetBlank",
          "url",
          "targetPath",
          "urlType",
          "configs",
          "banners.{isActive|startDate|endDate|image|targetPath|urlType}",
          "level"
        ]
      }
    });
    return result.data;
  }
  async getMenuItemById(categoryId) {
    const { data } = await this.discoveryApi.get(`/menu-item/${categoryId}`);
    return data;
  }
  async searchProductsByBarcode(reqParams) {
    const { sku, barcode, sellerId, terminalCode } = reqParams;
    const params = {
      identifiers: [
        {
          sellerSkuIdentifier: {
            sellerId,
            sellerSku: sku
          }
        },
        {
          sellerBarcodeIdentifier: {
            barcode,
            sellerId
          }
        }
      ],
      terminalCode
    };
    const { data } = await this.discoveryApi.post(`/products`, params);
    return data;
  }
  async getCollectionsByPlatformId(params) {
    const { data } = await this.discoveryApi.get("/collections", { params: __spreadValues({}, params) });
    return data;
  }
}
const normalizeDiscoveryApiParams = (params) => {
  const listingAndDiscoveryFieldsMap = {
    _sort: "sort",
    _order: "order",
    bestPrice_gte: "priceGte",
    bestPrice_lte: "priceLte",
    price_lte: "priceLte",
    price_gte: "priceGte",
    _limit: "itemsPerPage",
    limit: "itemsPerPage",
    sellerCategories: "masterCategories",
    _page: "pageNumber",
    page: "pageNumber",
    q: "query",
    keyword: "query",
    skus_ne: "excludedSkus",
    providerId: "providerIds"
  };
  const normalizedParams = {};
  Object.keys(params).forEach((field) => {
    if (listingAndDiscoveryFieldsMap[field]) {
      normalizedParams[listingAndDiscoveryFieldsMap[field]] = params[field];
    } else {
      normalizedParams[field] = params[field];
    }
  });
  return normalizedParams;
};
const convertArrayValue = (value, returnType = "string") => {
  if (typeof value === "string") {
    if (returnType === "string")
      return value.split(",");
    return value.split(",").map((el) => +el);
  }
  if (returnType === "string")
    return value;
  return value.map((el) => +el);
};

export { DiscoveryService };
